"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { Tour } from "nextstepjs";
import { Icon, Text } from "@chakra-ui/react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { jobStepsSelector } from "../jobTourSelectors";
import { FeatureTour, POINTER_RADIUS, POINTER_PADDING } from "../constant";
import { LuFilter, LuLayoutPanelLeft, LuPlus, LuSearch } from "react-icons/lu";
import {
  FaJetFighterUp,
  FaUpRightAndDownLeftFromCenter,
} from "react-icons/fa6";
import { TbViewportShort } from "react-icons/tb";
import { WistiaPlayer } from "@wistia/wistia-player-react";
import { WISTIA_MEDIA_ID } from "../../GroupCandidates/constant";

export const useJobTour = (): Tour => {
  const t = useTranslations("manage_job.feature_tour");

  return useMemo(
    () => ({
      tour: FeatureTour.JobManagement,
      steps: [
        {
          icon: <LuLayoutPanelLeft />,
          title: t("step1.title"),
          showControls: true,
          showSkip: true,
          side: "right",
          selector: jobStepsSelector.sidebar.selector,
          content: t("step1.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <LuSearch />,
          title: t("step2.title"),
          showControls: true,
          showSkip: true,
          side: "bottom",
          selector: jobStepsSelector.searchJobs.selector,
          content: t("step2.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <LuFilter />,
          title: t("step3.title"),
          showControls: true,
          showSkip: true,
          side: "bottom",
          selector: jobStepsSelector.filterJobs.selector,
          content: t("step3.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <LuPlus />,
          title: t("step4.title"),
          showControls: true,
          showSkip: true,
          side: "bottom",
          selector: jobStepsSelector.newJob.selector,
          content: (
            <Text textStyle="sm" color="neutral.700" mb={4}>
              {t("step4.content")}{" "}
              <Text as="span" fontWeight="bold" color="neutral.800">
                {t("step4.bold")}
              </Text>
            </Text>
          ),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <FaJetFighterUp />,
          title: t("step5.title"),
          showControls: true,
          showSkip: true,
          side: "right",
          selector: jobStepsSelector.jobItem.selector,
          content: (
            <Text textStyle="sm" color="neutral.700" mb={2}>
              {t("step5.before_icon")}{" "}
              <Icon
                as={BsThreeDotsVertical}
                boxSize={4}
                display="inline-block"
                verticalAlign="middle"
                mb="2px"
                mx={0.5}
                color="primary.500"
                aria-hidden
              />{" "}
              {t("step5.after_icon")}
            </Text>
          ),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <FaUpRightAndDownLeftFromCenter />,
          title: t("step6.title"),
          showControls: true,
          showSkip: true,
          side: "top",
          selector: jobStepsSelector.navigationJobs.selector,
          content: t("step6.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <TbViewportShort />,
          title: t("step7.title"),
          showControls: true,
          showSkip: true,
          side: "left",
          selector: jobStepsSelector.jobDetail.selector,
          content: (
            <>
              <Text textStyle="sm" color="neutral.800" mb={2}>
                {t("step7.content")}
              </Text>
              <Text textStyle="xs" color="neutral.600" mb={2}>
                {t("step7.video_hint")}
              </Text>
              <WistiaPlayer
                mediaId={WISTIA_MEDIA_ID.jobManagement}
                autoplay
                qualityMin={720}
              />
            </>
          ),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
      ],
    }),
    [t],
  );
};
